/**
 * ReviewsSlider Web Component
 *
 * A web component for horizontally scrolling reviews with the following features:
 * - Continuous scrolling with infinite loop
 * - Configurable speed and direction
 * - Slows down on hover
 * - Smooth acceleration and deceleration
 */

class CustomStyleReviewsSlider extends HTMLElement {
  constructor() {
    super();
    this.isAnimating = false;
    this.animationFrameId = null;
    this.position = 0;
    this.isHovering = false;
    this.cloneCount = 0;
    this.itemSetWidth = 0;
    this.totalWidth = 0;
    this.direction = 'rtl';
    this.speed = 120;
    this.resizeObserver = null;
    this.lastTimestamp = 0;
    this.allItemsLoaded = false;
    this.imagesLoaded = 0;
    this.totalImages = 0;
    this.sectionId = this.getAttribute('data-section-id');

    // Animation state for smooth transitions
    this.currentSpeed = 0;
    this.targetSpeed = 0;
    this.isTransitioning = false;
    this.transitionStartTime = 0;
    this.transitionDuration = 800; // Increased to 800ms for smoother transitions

    // Drag state variables
    this.isDragging = false;
    this.startX = 0;
    this.startPosition = 0;
    this.lastDragX = 0;
    this.dragVelocity = 0;
    this.lastDragTime = 0;

    // Bind event handlers for proper cleanup
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onSectionUnload = this.onSectionUnload.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragMove = this.handleDragMove.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.handleSmoothScrollClick = this.handleSmoothScrollClick.bind(this);
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }

    this.addEventListener('click', this.handleSmoothScrollClick);

    // Add Shopify section event listeners
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:section:unload', this.onSectionUnload);
  }

  disconnectedCallback() {
    this.cleanup();
    this.removeEventListener('click', this.handleSmoothScrollClick);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('shopify:section:unload', this.onSectionUnload);
  }

  handleSmoothScrollClick(event) {
    const link = event.target.closest('a[data-reviews-slider-smooth-scroll="true"]');
    if (!link) return;

    try {
      const url = new URL(link.getAttribute('href') || '', window.location.href);
      const isSamePage =
        url.origin === window.location.origin &&
        url.pathname === window.location.pathname &&
        url.search === window.location.search;

      if (!isSamePage || !url.hash || url.hash === '#') return;

      const targetId = decodeURIComponent(url.hash.slice(1));
      const target = document.getElementById(targetId);
      if (!target) return;

      event.preventDefault();

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
      history.pushState(null, '', url.hash);
    } catch (e) {
      // If URL parsing fails, fall back to default link behavior.
    }
  }

  init() {
    // Get references to DOM elements
    this.track = this.querySelector('.reviews-slider__swiper');
    this.wrapper = this.querySelector('.reviews-slider__grid');

    if (!this.track || !this.wrapper) {
      return;
    }

    // Get configuration from data attributes or use defaults
    this.direction = this.getAttribute('data-scroll-direction') || 'rtl';
    this.speed = parseInt(this.getAttribute('data-scroll-speed') || '120', 10);

    // Setup styles for continuous scrolling
    this.setupSliderStyles();

    // Wait for images to load before starting animation
    this.waitForImages()
      .then(() => {
        // Clone items for infinite scrolling
        this.cloneItems();

        // Calculate dimensions for animation
        this.calculateDimensions();

        // Set up event listeners
        this.setupEventListeners();

        // Set initial position
        this.resetPosition();

        // Start animation with a short delay to ensure everything is ready
        setTimeout(() => {
          this.startAnimation();
        }, 100);
      })
      .catch((error) => {
        // If there's an error, try to continue anyway
        this.cloneItems();
        this.calculateDimensions();
        this.setupEventListeners();
        this.resetPosition();
        this.startAnimation();
      });
  }

  waitForImages() {
    return new Promise((resolve) => {
      const items = this.querySelectorAll('.reviews-slider__card');
      const images = this.querySelectorAll('img');

      this.totalImages = images.length;

      // If no images, resolve immediately
      if (this.totalImages === 0) {
        this.allItemsLoaded = true;
        resolve();
        return;
      }

      // Wait for actual images to load
      images.forEach((img) => {
        if (img.complete) {
          this.imagesLoaded++;
        } else {
          img.onload = () => {
            this.imagesLoaded++;
            if (this.imagesLoaded >= this.totalImages) {
              this.allItemsLoaded = true;
              resolve();
            }
          };

          img.onerror = () => {
            this.imagesLoaded++;
            if (this.imagesLoaded >= this.totalImages) {
              this.allItemsLoaded = true;
              resolve();
            }
          };
        }

        // Safety fallback - if images take too long, proceed anyway
        setTimeout(() => {
          if (!this.allItemsLoaded) {
            this.allItemsLoaded = true;
            resolve();
          }
        }, 2000);
      });

      // If all images are already loaded
      if (this.imagesLoaded >= this.totalImages) {
        this.allItemsLoaded = true;
        resolve();
      }
    });
  }

  setupSliderStyles() {
    // Set container to hide overflow
    this.track.style.overflow = 'hidden';
    this.track.style.width = '100%';

    // Set styles for continuous animation
    this.wrapper.style.display = 'inline-flex';
    this.wrapper.style.boxSizing = 'border-box';
    this.wrapper.style.position = 'relative';
    this.wrapper.style.willChange = 'transform';
    // Prevent flickering
    this.wrapper.style.backfaceVisibility = 'hidden';
    // Disable transitions for smoother animation
    this.wrapper.style.transition = 'none';
  }

  cloneItems() {
    // Get original items
    const items = this.querySelectorAll('.reviews-slider__card');
    if (items.length === 0) {
      return;
    }

    // Remove any existing clones first (in case of resize/reinit)
    const clones = this.querySelectorAll('.reviews-slider__card.clone');
    clones.forEach((clone) => clone.remove());

    // Calculate how many sets of clones we need based on viewport width
    // We need at least 2 complete sets to ensure seamless looping
    const viewportWidth = window.innerWidth;
    const setsNeeded = Math.max(2, Math.ceil((viewportWidth * 2) / (items[0].offsetWidth * items.length)));
    this.cloneCount = setsNeeded;

    // Process original items for proper display
    items.forEach((item) => {
      // Style all items for consistent display
      item.style.flex = '0 0 auto';
      item.style.marginRight = '24px';
      // Don't set minWidth to max-content as it can cause sizing issues
      // Let CSS handle the width constraints
    });

    // Clone items multiple times to ensure no blank space
    // First, create full sets in front
    for (let i = 0; i < setsNeeded; i++) {
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.classList.add('clone');
        // Apply same styling to clones
        clone.style.flex = '0 0 auto';
        clone.style.marginRight = '24px';
        this.wrapper.appendChild(clone);
      });
    }

    // Then, create full sets at the beginning
    for (let i = 0; i < setsNeeded; i++) {
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.classList.add('clone');
        // Apply same styling to clones
        clone.style.flex = '0 0 auto';
        clone.style.marginRight = '24px';
        // Insert at the beginning
        this.wrapper.insertBefore(clone, this.wrapper.firstChild);
      });
    }
  }

  calculateDimensions() {
    // Get all items (original + clones)
    const allItems = this.querySelectorAll('.reviews-slider__card');

    // Calculate the width of a single set (original items)
    const originalItems = this.querySelectorAll('.reviews-slider__card:not(.clone)');

    // Calculate the total width of all items for animation
    this.totalWidth = 0;
    let singleSetWidth = 0;

    // First measure original set width
    originalItems.forEach((item) => {
      const itemWidth = item.offsetWidth;
      singleSetWidth += itemWidth;
    });

    this.itemSetWidth = singleSetWidth;

    // Then get total width
    allItems.forEach((item) => {
      this.totalWidth += item.offsetWidth;
    });

    // Make sure we have enough clones to prevent gaps
    if (this.totalWidth < window.innerWidth * 2) {
      // Add more clones if needed
      const additionalSetsNeeded = Math.ceil((window.innerWidth * 2 - this.totalWidth) / this.itemSetWidth);

      if (additionalSetsNeeded > 0) {
        const originalItemsForCloning = this.querySelectorAll('.reviews-slider__card:not(.clone)');

        for (let i = 0; i < additionalSetsNeeded; i++) {
          originalItemsForCloning.forEach((item) => {
            const clone = item.cloneNode(true);
            clone.classList.add('clone');
            this.wrapper.appendChild(clone);
          });
        }

        // Recalculate total width
        this.totalWidth = 0;
        this.querySelectorAll('.reviews-slider__card').forEach((item) => {
          this.totalWidth += item.offsetWidth;
        });
      }
    }
  }

  setupEventListeners() {
    // Add hover event listeners to stop animation completely
    this.track.addEventListener('mouseenter', this.handleMouseEnter);
    this.track.addEventListener('mouseleave', this.handleMouseLeave);

    // Add drag event listeners
    this.wrapper.addEventListener('mousedown', this.handleDragStart);
    this.wrapper.addEventListener('touchstart', this.handleDragStart, { passive: true });

    // Handle page visibility change
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Observe resize events to recalculate dimensions
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this);
  }

  handleDragStart(e) {
    // Prevent default behavior only for mouse events
    if (e.type === 'mousedown') {
      e.preventDefault();
    }

    // Get the initial touch/mouse position
    const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;

    this.isDragging = true;
    this.startX = clientX;
    this.lastDragX = clientX;
    this.startPosition = this.position;
    this.lastDragTime = performance.now();

    // Stop the animation while dragging
    this.smoothStop();

    // Add move and end event listeners
    if (e.type === 'mousedown') {
      document.addEventListener('mousemove', this.handleDragMove);
      document.addEventListener('mouseup', this.handleDragEnd);
    } else {
      document.addEventListener('touchmove', this.handleDragMove, { passive: true });
      document.addEventListener('touchend', this.handleDragEnd);
      document.addEventListener('touchcancel', this.handleDragEnd);
    }

    // Change cursor style
    this.wrapper.style.cursor = 'grabbing';
  }

  handleDragMove(e) {
    if (!this.isDragging) return;

    // Get current touch/mouse position
    const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    const currentTime = performance.now();
    const timeDelta = currentTime - this.lastDragTime;

    // Calculate movement
    const deltaX = clientX - this.lastDragX;

    // Update position based on drag
    this.position = this.position + deltaX;

    // Calculate velocity for momentum
    if (timeDelta > 0) {
      this.dragVelocity = deltaX / timeDelta;
    }

    // Handle infinite loop while dragging
    if (this.position <= -this.totalWidth / 3) {
      this.position += this.totalWidth / 3;
      this.startPosition += this.totalWidth / 3;
      this.startX = clientX;
    } else if (this.position >= 0) {
      this.position -= this.totalWidth / 3;
      this.startPosition -= this.totalWidth / 3;
      this.startX = clientX;
    }

    // Apply the new position
    this.wrapper.style.transform = `translate3d(${this.position}px, 0px, 0px)`;

    // Update last position and time for next move
    this.lastDragX = clientX;
    this.lastDragTime = currentTime;
  }

  // Event handler for mouse enter - smoothly stops animation
  handleMouseEnter() {
    this.isHovering = true;
    // Smoothly decelerate to a stop on hover for reliability
    this.smoothStop();
  }

  // Event handler for mouse leave - smoothly resumes animation
  handleMouseLeave() {
    this.isHovering = false;
    // Smoothly accelerate animation on mouse exit
    this.smoothStart();
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.lastTimestamp = performance.now();
      if (!this.isHovering) {
        this.smoothStart();
      }
    } else {
      this.smoothStop();
    }
  }

  handleResize() {
    // Fully reinitialize on resize to ensure correct dimensions
    this.stopAnimation();
    this.calculateDimensions();
    this.resetPosition();
    if (!this.isHovering) {
      this.smoothStart();
    }
  }

  resetPosition() {
    // Position the slider in the middle of the cloned sets
    // This ensures we have items on both sides for seamless looping
    const cloneCount = this.cloneCount;

    // For RTL, start at the first clone set
    // For LTR, start showing the last clone set
    if (this.direction === 'rtl') {
      this.position = -this.itemSetWidth * cloneCount;
    } else {
      this.position = -this.itemSetWidth * cloneCount;
    }

    // Apply the position immediately without animation
    this.wrapper.style.transition = 'none';
    this.wrapper.style.transform = `translate3d(${this.position}px, 0px, 0px)`;

    // Force reflow to ensure the transition is applied (using offsetWidth and not storing it)
    this.wrapper.getBoundingClientRect();
  }

  startAnimation() {
    // Don't start a new animation if one is already running
    if (this.isAnimating) {
      return;
    }

    console.log('Reviews Slider: Starting animation');
    this.isAnimating = true;
    this.lastTimestamp = performance.now();
    this.targetSpeed = this.speed;
    this.smoothStart();
  }

  smoothStart() {
    if (!this.isAnimating) {
      this.isAnimating = true;
      this.lastTimestamp = performance.now();
    }

    this.targetSpeed = this.speed;
    this.transitionStartTime = performance.now();
    this.isTransitioning = true;

    if (!this.animationFrameId) {
      this.animate(this.lastTimestamp);
    }
  }

  smoothStop() {
    this.targetSpeed = 0;
    this.transitionStartTime = performance.now();
    this.isTransitioning = true;
  }

  stopAnimation() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.isAnimating = false;
    this.currentSpeed = 0;
    this.targetSpeed = 0;
    this.isTransitioning = false;
  }

  // Easing function for smooth transitions
  easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Smoother easing function for more natural transitions
  easeInOutQuart(t) {
    return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
  }

  animate(timestamp) {
    // Calculate time delta for smooth animation regardless of frame rate
    const delta = timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;

    // Handle smooth speed transitions
    if (this.isTransitioning) {
      const elapsed = timestamp - this.transitionStartTime;
      const progress = Math.min(elapsed / this.transitionDuration, 1);
      const easedProgress = this.easeInOutQuart(progress);

      // More gradual interpolation for smoother transitions
      const speedDiff = this.targetSpeed - this.currentSpeed;
      this.currentSpeed += speedDiff * 0.08; // Slower interpolation for smoother feel

      // If transition is complete
      if (progress >= 1) {
        this.isTransitioning = false;
        this.currentSpeed = this.targetSpeed;

        // If target speed is 0 and we're not hovering, stop animation
        if (this.targetSpeed === 0 && !this.isHovering) {
          this.stopAnimation();
          return;
        }
      }
    }

    // Use delta to ensure consistent speed regardless of frame rate
    // This prevents stutter on different devices or when the tab loses focus
    // Normalize to 60fps
    const timeRatio = delta / (1000 / 60);

    // Use current speed for animation (which smoothly transitions)
    const pixelsToMove = (this.currentSpeed / 60) * timeRatio;

    // Move position based on direction
    if (this.direction === 'rtl') {
      this.position -= pixelsToMove;

      // If we've moved past the entire set, reset by total content width
      if (this.position <= -this.totalWidth / 3) {
        this.position += this.totalWidth / 3;
      }
    } else {
      this.position += pixelsToMove;

      if (this.position >= 0) {
        this.position -= this.totalWidth / 3;
      }
    }

    // Apply transform with translate3d for smooth GPU-accelerated animation
    this.wrapper.style.transform = `translate3d(${this.position}px, 0px, 0px)`;

    // Continue the animation
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }

  handleDragEnd(e) {
    if (!this.isDragging) return;

    this.isDragging = false;

    // Remove move and end event listeners
    document.removeEventListener('mousemove', this.handleDragMove);
    document.removeEventListener('mouseup', this.handleDragEnd);
    document.removeEventListener('touchmove', this.handleDragMove);
    document.removeEventListener('touchend', this.handleDragEnd);
    document.removeEventListener('touchcancel', this.handleDragEnd);

    // Reset cursor style
    this.wrapper.style.cursor = '';

    // if (Math.abs(this.dragVelocity) > 0.1) {
    //   this.direction = this.dragVelocity < 0 ? 'rtl' : 'ltr';
    // }

    // Resume animation with momentum
    if (!this.isHovering) {
      this.smoothStart();
    }
  }

  cleanup() {
    this.stopAnimation();

    // Remove event listeners properly with stored references
    if (this.track) {
      this.track.removeEventListener('mouseenter', this.handleMouseEnter);
      this.track.removeEventListener('mouseleave', this.handleMouseLeave);
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Remove drag event listeners
    if (this.wrapper) {
      this.wrapper.removeEventListener('mousedown', this.handleDragStart);
      this.wrapper.removeEventListener('touchstart', this.handleDragStart);
    }
    document.removeEventListener('mousemove', this.handleDragMove);
    document.removeEventListener('mouseup', this.handleDragEnd);
    document.removeEventListener('touchmove', this.handleDragMove);
    document.removeEventListener('touchend', this.handleDragEnd);
    document.removeEventListener('touchcancel', this.handleDragEnd);

    // Disconnect resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  onSectionLoad(e) {
    if (e.detail && e.detail.sectionId === this.sectionId) {
      this.cleanup();
      this.init();
    }
  }

  onSectionUnload(e) {
    if (e.detail && e.detail.sectionId === this.sectionId) {
      this.cleanup();
    }
  }
}

// Register the custom element if it hasn't been registered already
if (!customElements.get('custom-style-reviews-slider')) {
  customElements.define('custom-style-reviews-slider', CustomStyleReviewsSlider);
}